import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { AuthProvider, DevUser, GoalsResponse, HistoryEntry, LoansResponse, Me, SummaryResponse, UserSummary, AdminUser } from "./types";
import { cacheEpoch, ingestFetched, resetLoanCache } from "./loanCache";
import { displayStages } from "./loanSteps";

/** `assigned`: pilot roles that read only loans assigned to them (server-filtered). */
export type Scope = "mine" | "team" | "all" | "assigned";

export const qk = {
  me: ["me"] as const,
  users: ["users"] as const,
  loansAll: ["loans"] as const,
  loans: (scope: Scope, teamId: string | null, lo: string | null = null) =>
    lo ? (["loans", scope, teamId, lo] as const) : (["loans", scope, teamId] as const),
  history: (id: string) => ["history", id] as const,
  summary: (teamId: string | null, month: string) => ["summary", teamId, month] as const,
  goals: (year: number, teamId: string | null) => ["goals", year, teamId] as const,
  adminUsers: ["admin", "users"] as const,
  devUsers: ["dev", "users"] as const,
  authProviders: ["dev", "auth-providers"] as const,
  teams: ["people", "teams"] as const,
  teamMembersAll: ["people", "members"] as const,
  teamMembers: (teamId: string | null) => ["people", "members", teamId] as const,
  invites: ["people", "invites"] as const,
  unassigned: ["people", "unassigned"] as const,
};

/** /api/me with the lender's stage display names (lib/loanSteps.ts), so no screen shows a stage label the lender doesn't use. */
export function meForDisplay(raw: Me): Me {
  return { ...raw, stages: displayStages(raw.stages) };
}

let principal: string | null = null;
export function useMeQuery() {
  const qc = useQueryClient();
  return useQuery({ queryKey: qk.me, queryFn: async () => {
    try {
      const me = meForDisplay(await api<Me>("/api/me"));
      const key = JSON.stringify(me.user);
      if (principal !== key) {
        principal = key;
        resetLoanCache();
        qc.removeQueries({ predicate: (q) => !["me", "dev"].includes(String(q.queryKey[0])) });
      }
      return me;
    } catch (e) {
      resetLoanCache(); principal = null;
      qc.removeQueries({ predicate: (q) => !["me", "dev"].includes(String(q.queryKey[0])) });
      throw e;
    }
  }, retry: false, staleTime: 60_000 });
}

/** Only call inside the authenticated shell. */
export function useMe(): Me {
  const q = useMeQuery();
  if (!q.data) throw new Error("useMe used outside the authenticated shell");
  return q.data;
}

export function useUsers() {
  return useQuery({ queryKey: qk.users, queryFn: () => api<UserSummary[]>("/api/users"), staleTime: 5 * 60_000 });
}

/** `lo`: one directory LO's loans, every one the caller can view whatever the scope (server rule). */
export function loansUrl(scope: Scope, teamId: string | null, lo: string | null = null) {
  const p = new URLSearchParams({ scope });
  if (teamId) p.set("team_id", teamId);
  if (lo) p.set("lo_person_id", lo);
  return `/api/loans?${p}`;
}

export function useLoans(scope: Scope, teamId: string | null = null, enabled = true, lo: string | null = null) {
  return useQuery({
    queryKey: qk.loans(scope, teamId, lo),
    queryFn: async ({ signal }) => {
      const epoch = cacheEpoch();
      const response = await api<LoansResponse>(loansUrl(scope, teamId, lo), { signal });
      if (epoch !== cacheEpoch()) throw new Error("Session changed");
      return ingestFetched(response);
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useHistory(id: string | null) {
  return useQuery({
    queryKey: qk.history(id ?? ""),
    queryFn: () => api<HistoryEntry[]>(`/api/loans/${id}/history`),
    enabled: !!id,
  });
}

export function useSummary(teamId: string | null, month: string) {
  return useQuery({
    queryKey: qk.summary(teamId, month),
    queryFn: () => {
      const p = new URLSearchParams({ month });
      if (teamId) p.set("team_id", teamId);
      return api<SummaryResponse>(`/api/summary?${p}`);
    },
    placeholderData: (prev) => prev,
  });
}

export function useGoals(year: number, teamId: string | null, enabled = true) {
  return useQuery({
    queryKey: qk.goals(year, teamId),
    queryFn: () => {
      const p = new URLSearchParams({ year: String(year) });
      if (teamId) p.set("team_id", teamId);
      return api<GoalsResponse>(`/api/goals?${p}`);
    },
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useAdminUsers(enabled: boolean) {
  return useQuery({ queryKey: qk.adminUsers, queryFn: () => api<AdminUser[]>("/api/admin/users"), enabled });
}

export function useDevUsers() {
  return useQuery({ queryKey: qk.devUsers, queryFn: () => api<DevUser[]>("/api/dev/users"), retry: false, staleTime: Infinity });
}

/** Public; empty in dev mode. A failure just hides the secondary sign-in links. */
export function useAuthProviders() {
  return useQuery({ queryKey: qk.authProviders, queryFn: () => api<AuthProvider[]>("/api/auth/providers"), retry: false, staleTime: Infinity });
}

export function refetchEverything(qc: QueryClient) {
  return qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "dev" });
}
